/**
 * Admin API Service
 *
 * Separate from the main api.js — uses admin session tokens stored in
 * sessionStorage (cleared automatically when the browser tab closes).
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STORAGE_KEY = 'invilic_admin_token';

export function getAdminToken() {
  return sessionStorage.getItem(STORAGE_KEY);
}

export function saveAdminToken(token) {
  sessionStorage.setItem(STORAGE_KEY, token);
}

export function clearAdminToken() {
  sessionStorage.removeItem(STORAGE_KEY);
}

async function adminFetch(path, token, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  if (res.status === 401) {
    clearAdminToken();
    throw new Error('SESSION_EXPIRED');
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `Request failed: ${res.status}`);
  }

  return res.json();
}

export async function adminLogin(password) {
  const res = await fetch(`${API_BASE}/api/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || 'Login failed');
  }

  return res.json();
}

export const fetchOverview = (token, year, month) =>
  adminFetch(`/api/admin/overview?year=${year}&month=${month}`, token);

export const fetchUsers = (token, page = 1, q = '', planFilter = '') =>
  adminFetch(
    `/api/admin/users?page=${page}&q=${encodeURIComponent(q)}&plan_filter=${planFilter}`,
    token,
  );

export const fetchCharts = (token) =>
  adminFetch('/api/admin/charts', token);

export const fetchActivity = (token) =>
  adminFetch('/api/admin/activity', token);

export const fetchEarnings = (token) =>
  adminFetch('/api/admin/earnings', token);

export const fetchEmailStats = (token) =>
  adminFetch('/api/admin/emails', token);

export const fetchAnalytics = (token, days = 30) =>
  adminFetch(`/api/admin/analytics?days=${days}`, token);
